import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from './ui/dialog';
import { 
  HelpCircle, 
  Search, 
  ChevronRight, 
  Home, 
  FileText, 
  Users, 
  Star,
  Sparkles,
  BookOpen,
  MessageSquare,
  CheckCircle2,
  X
} from 'lucide-react';

const HelpCenter = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeSection, setActiveSection] = useState('whats-new');
  const [expandedFaq, setExpandedFaq] = useState(null);

  const whatsNew = [
    {
      id: 'wn-2026-02-banner',
      date: 'February 2026',
      title: "What's New Banners",
      description: 'Active features now ship with a floating bottom-right banner so you never miss what changed. Tap "Got it" to dismiss; tap the CTA to jump straight to the feature.',
      badge: 'New',
      badgeColor: 'bg-emerald-500'
    },
    {
      id: 'wn-2026-02-ai-synthesis',
      date: 'February 2026',
      title: 'AI-Drafted Review Synthesis',
      description: 'On the manager-synthesis step of a 5-Star review, click "Generate AI Summary" to have Claude Sonnet 4.5 weave the Manager + Area Manager inputs into a personalized synthesis that also references the employee\'s historical review trajectory. Always edit before signing off — the AI is a starting draft.',
      badge: 'New',
      badgeColor: 'bg-emerald-500'
    },
    {
      id: 'wn-2026-02-pdf-export',
      date: 'February 2026',
      title: 'Export 5-Star Reviews to PDF',
      description: 'Every completed 5-Star review has a Download PDF button on the Results page — perfect for HR files, signatures, or sharing offline.',
      badge: 'New',
      badgeColor: 'bg-emerald-500'
    },
    {
      id: 'wn-2026-02-admin-hub',
      date: 'February 2026',
      title: 'Admin Hub — One Place for Everything',
      description: 'The new /admin page replaces the password-protected admin panel. Super Admins now manage Analytics, Activity Logs, Manager Invites, Impersonate, Review Initiators, Templates (Facility Overview + TAH), Reports/PowerBI, and What\'s New Banners — all from a single Microsoft-SSO-protected page. Roles can be delegated per area (Template Admin, Reports Admin, Review Initiator).',
      badge: 'New',
      badgeColor: 'bg-emerald-500'
    },
    {
      id: 'wn-2026-02-daily-sync',
      date: 'February 2026',
      title: 'Automatic Review-Initiator Sync',
      description: 'The DOR + AM Entra ID groups now sync every day at 06:00 — anyone added to either group automatically gains the ability to initiate 5-Star reviews. A manual "Sync from Entra ID" button is still available on the Review Initiators tab.',
      badge: 'New',
      badgeColor: 'bg-emerald-500'
    },
    {
      id: 'wn-2026-02-review-cycles',
      date: 'February 2026',
      title: 'Review Types & Cycle Names',
      description: 'When initiating a 5-Star review, pick a Type (Introductory / Bi-Annual / AdHoc) and give the cycle a clear name (e.g. "90-Day Introductory", "2026 H1 Bi-Annual"). The chosen Type drives a smart default name and shows on every screen.',
      badge: 'Improved',
      badgeColor: 'bg-blue-500'
    },
    {
      id: 'wn-2026-02-strengths-growth',
      date: 'February 2026',
      title: 'Strengths & Growth Areas',
      description: 'Step 1 (Manager Rating) and the Synthesis step now capture "Strengths" and "Growth Areas" alongside the per-category comments. These flow through to the Results view as a SWOT panel for both manager and employee.',
      badge: 'Improved',
      badgeColor: 'bg-blue-500'
    },
    {
      id: 'wn-2026-02-mandatory-comments',
      date: 'February 2026',
      title: 'Per-Category Comments Are Required',
      description: 'Every 1–5 rating now requires a comment so feedback is actionable. Empty categories are caught before the form submits and you\'re shown exactly which ones are missing.',
      badge: 'Improved',
      badgeColor: 'bg-blue-500'
    },
    {
      id: 'wn-2026-02-am-opt-out',
      date: 'February 2026',
      title: 'Area Manager Opt-Out',
      description: 'On the Area Manager step there\'s now an "Opt out of this review" button. The review moves straight to manager synthesis when an Area Manager isn\'t the right fit for that cycle.',
      badge: 'Improved',
      badgeColor: 'bg-blue-500'
    },
    {
      id: 'wn-2026-02-employee-view',
      date: 'February 2026',
      title: 'Employee Results View — Final Only',
      description: 'When an employee opens their completed review they now see only the FINAL synthesis and scores. The per-step Manager / Area Manager breakdown columns are hidden in the employee view — they\'re only visible to managers reviewing the audit trail.',
      badge: 'Improved',
      badgeColor: 'bg-blue-500'
    },
    {
      id: 'wn-2026-02-growthhub-domain',
      date: 'February 2026',
      title: 'New URL: growthhub.therapymgmt.com',
      description: 'The site is now reachable at both growthhub.therapymgmt.com and the original tacticals.therapymgmt.com. Same data, same login, same SSL cert — just a clearer URL that matches the new product.',
      badge: 'New',
      badgeColor: 'bg-emerald-500'
    },
    {
      id: 'wn-2025-05-growth-hub',
      date: 'May 2025',
      title: 'Growth Hub — New Home Experience',
      description: 'The app is now organized around the Growth Hub: a unified home with services for Tacticals and 5 Star Feedback. Use the left sidebar to switch between them at any time.',
      badge: 'Feature',
      badgeColor: 'bg-purple-500'
    },
    {
      id: 'wn-2025-05-five-star',
      date: 'May 2025',
      title: '5 Star Feedback & Development',
      description: 'Multi-stage performance review service. Managers initiate a review, optionally route it to an Area Manager, synthesize the final outcome, sign off, and capture the employee discussion with goals and follow-up.',
      badge: 'Feature',
      badgeColor: 'bg-purple-500'
    },
    {
      id: 'wn-2025-05-chevron',
      date: 'May 2025',
      title: 'Chevron Rating Selector',
      description: 'Star ratings have been replaced with a clear 1–5 chevron tile picker — easier to scan and click on every device.',
      badge: 'Improved',
      badgeColor: 'bg-blue-500'
    },
    {
      id: 'wn-2025-05-autosave',
      date: 'May 2025',
      title: 'Tactical Autosave',
      description: 'Your tactical saves automatically as you type. Watch the indicator next to the Save button: amber means unsaved, green means safely saved.',
      badge: 'Improved',
      badgeColor: 'bg-blue-500'
    },
    {
      id: 'wn-2025-03-tah',
      date: 'March 2025',
      title: 'TAH (Therapy @Home) Tracking',
      description: 'Track Therapy @Home activities. Enable TAH from the Facility Overview section to add a dedicated tab for home therapy tracking.',
      badge: 'Feature',
      badgeColor: 'bg-purple-500'
    },
    {
      id: 'wn-2025-03-sites',
      date: 'March 2025',
      title: 'Multiple Facility Sites',
      description: 'Create multiple facility tabs within your tactical to manage different locations or departments separately.',
      badge: 'Feature',
      badgeColor: 'bg-purple-500'
    }
  ];

  const faqs = [
    {
      category: 'Getting Started',
      icon: <Home className="w-4 h-4" />,
      questions: [
        {
          q: 'How do I log in?',
          a: 'Sign in with your @therapymgmt.com Microsoft account from the login page. In some preview environments you may see a "Preview Mode" sign-in panel instead — click any user or enter an email to sign in.'
        },
        {
          q: 'What is the Growth Hub?',
          a: 'The Growth Hub is the home page of the app. It surfaces all the services available to you — currently Tacticals (monthly performance tracking) and 5 Star Feedback (multi-stage performance reviews). Use the left sidebar to jump between them anytime.'
        },
        {
          q: 'How do I create my first tactical?',
          a: 'From the Growth Hub home, click the Tacticals service card (or use the sidebar). On the Tacticals dashboard click "Create Tactical" — this creates a tactical for the current month with all standard sections ready to fill in.'
        },
        {
          q: 'Does my tactical work save automatically?',
          a: 'Yes. As you edit, a status pill next to the Save button shows: Amber "Unsaved changes" while you type, then Green "All changes saved" once the autosave completes (about 1.5 seconds after you stop). You can also click Save at any time. If you close the tab with pending edits, the browser will warn you.'
        }
      ]
    },
    {
      category: '5 Star Feedback',
      icon: <Star className="w-4 h-4" />,
      questions: [
        {
          q: 'What is 5 Star Feedback?',
          a: 'A structured performance review service. A review moves through 5 stages: Manager Rates → Area Manager Reviews → Manager Synthesizes → Final Sign-off → Employee Discussion. Each stage is gated to the right person at the right time.'
        },
        {
          q: 'Who can initiate a review?',
          a: 'Anyone with the Review Initiator role. The role is auto-granted to members of the DORCompanyWide and AMsCompanyWide Entra ID groups (synced daily at 06:00). Super Admins can also grant the role manually from /admin → Review Initiators → Add manually. Super Admins themselves can always initiate.'
        },
        {
          q: 'How do I start a review for a team member?',
          a: 'Open "5 Star Feedback" from the sidebar and click "Initiate New Review". Pick the employee, optionally pick an Area Manager (you cannot pick yourself for either role), choose a Review Type (Introductory / Bi-Annual / AdHoc), and give the cycle a Name (e.g. "90-Day Introductory" or "2026 H1 Bi-Annual"). Submit and you\'re routed straight into the rating screen.'
        },
        {
          q: 'How does the rating work?',
          a: 'Each category is rated on a 1–5 chevron tile scale. Click a number to select; click again to change. Comments are REQUIRED per category and an overall comment is required at the bottom. Step 1 (Manager Rating) and Step 3 (Synthesis) additionally capture Strengths and Growth Areas — one item per line.'
        },
        {
          q: 'What does the Area Manager do?',
          a: 'The Area Manager reviews what the Manager wrote and can add comments. They don\'t have to re-rate every category — only adjust the ones where they want to push back or add context. They can also click "Opt out of this review" if they don\'t want to participate — the review jumps straight to Manager Synthesis.'
        },
        {
          q: 'What is the AI Summary on the Synthesis step?',
          a: 'On Step 3 (Manager Synthesizes) click "Generate AI Summary" and Claude Sonnet 4.5 will draft a personalized synthesis by combining the Manager + Area Manager inputs AND the employee\'s historical review trajectory. ALWAYS read and edit the draft before signing off — the AI is a starting point, not a final word.'
        },
        {
          q: 'Can I export a completed review?',
          a: 'Yes. On the Results page of a discussed (completed) review, click "Download PDF". A polished 3-page PDF with the final scores, SWOT panel, trend chart, and discussion summary downloads instantly.'
        },
        {
          q: 'What happens during the Employee Discussion step?',
          a: 'After sign-off, the manager (or employee) marks the discussion complete. The form captures Discussion Notes, Agreed Goals & Action Items (one per line), an optional Follow-up date, and Employee Comments. An acknowledgment checkbox is required before archiving. All of this is preserved for trend tracking.'
        },
        {
          q: 'What does the employee see on a completed review?',
          a: 'Only the final synthesis, final composite scores, the SWOT panel, and the discussion summary. The Manager and Area Manager step-by-step columns are intentionally hidden in the employee view to keep the conversation focused on the agreed outcome. Managers reviewing the audit trail still see the full breakdown.'
        },
        {
          q: 'Can I run more than one review for the same person?',
          a: 'Yes — but only one active review at a time per employee. Initiating a new review automatically archives any prior active review for that employee. Past reviews remain accessible for trend comparison.'
        },
        {
          q: 'Where do I see reviews about me?',
          a: 'In the 5 Star Feedback page, switch from "As Manager / Area Manager" to the "My Reviews" tab. You\'ll see all reviews where you\'re the employee (in-progress and archived).'
        }
      ]
    },
    {
      category: "What's New & Notifications",
      icon: <Sparkles className="w-4 h-4" />,
      questions: [
        {
          q: "What's the floating banner in the bottom-right?",
          a: 'That\'s a "What\'s New" banner — Super Admins use it to spotlight new features. Tap "Got it" to dismiss permanently (you\'ll never see that specific banner again). If the banner has a Check it out CTA, tapping it navigates straight to the feature AND dismisses in one step.'
        },
        {
          q: "Why do I see different banners than my colleague?",
          a: 'Dismissals are per-user. Once you tap "Got it", that banner is gone for you specifically. Your colleague still sees it until they dismiss too. A Super Admin can also deactivate or delete a banner globally from /admin → What\'s New.'
        },
        {
          q: 'I miss the old announcements pop-up. Where did it go?',
          a: 'The blocking pop-up announcements were replaced by the non-blocking What\'s New banner because the banner is friendlier (you can keep working), deep-links to the new feature, and stops nagging once you\'ve seen each item.'
        }
      ]
    },
    {
      category: 'Admin & Roles',
      icon: <Users className="w-4 h-4" />,
      questions: [
        {
          q: 'How do I get to the Admin Hub?',
          a: 'Sign in normally with Microsoft SSO. If you have any admin role, an "Admin" entry appears in the left sidebar — click it. The legacy password-protected page at /admin-legacy is being retired and will be removed once parity is confirmed.'
        },
        {
          q: 'What admin roles exist?',
          a: 'Super Admin (full control of everything, plus role grants), Template Admin (manages the Facility Overview + TAH templates), Reports Admin (access to PowerBI report endpoints + API key), and Review Initiator (can start 5-Star reviews and act as Area Manager).'
        },
        {
          q: "What's in the Admin Hub today?",
          a: 'My Roles · User Roles · Review Initiators · Templates (Facility Overview + TAH sub-section) · What\'s New banners · Analytics · Activity Logs · Manager Invites · Impersonate · Reports / PowerBI. All non-Super-Admin tabs respect the granular role gates.'
        },
        {
          q: 'Why does the Entra ID group sync run daily?',
          a: 'Members of DORCompanyWide and AMsCompanyWide should automatically be allowed to initiate 5-Star reviews. The daily 06:00 sync ensures recent hires and role changes are reflected without manual intervention. You can also click "Sync from Entra ID" anytime from the Review Initiators tab for an instant refresh.'
        },
        {
          q: 'Why do I see two URLs (tacticals + growthhub)?',
          a: 'Both tacticals.therapymgmt.com and growthhub.therapymgmt.com point to the same app. Use whichever you prefer — your data, login, and SSL cert are shared.'
        }
      ]
    },
    {
      category: 'Tacticals — Scorecard',
      icon: <FileText className="w-4 h-4" />,
      questions: [
        {
          q: 'What do the colors mean?',
          a: 'Green = On Track (meeting or exceeding targets), Yellow = Caution (slightly below, needs attention), Red = At Risk (significantly below, requires immediate action).'
        },
        {
          q: 'How often should I update my scorecard?',
          a: 'We recommend updating weekly to maintain an accurate picture of your facility\'s performance throughout the month.'
        }
      ]
    },
    {
      category: 'Tacticals — Facility Overview',
      icon: <FileText className="w-4 h-4" />,
      questions: [
        {
          q: 'How do I add a new facility tab?',
          a: 'Click the "+" button next to your existing tabs. Enter a name for the new facility and click "Add". Each tab has its own set of questions.'
        },
        {
          q: 'Can I rename or delete a facility tab?',
          a: 'Yes. Click the pencil icon to rename, or the X icon to delete a tab. You cannot delete your last remaining tab.'
        },
        {
          q: 'Can I reorder my facility tabs?',
          a: 'Yes — drag and drop the tabs to arrange them in your preferred order.'
        }
      ]
    },
    {
      category: 'TAH (Therapy @Home)',
      icon: <Home className="w-4 h-4" />,
      questions: [
        {
          q: 'What is TAH?',
          a: 'TAH (Therapy @Home) is a special section for tracking home therapy activities. It\'s optional and can be enabled each month as needed.'
        },
        {
          q: 'How do I enable TAH?',
          a: 'In the Facility Overview section, check the "TAH (Therapy @Home)" checkbox. This creates a dedicated TAH tab with specific tracking questions.'
        },
        {
          q: 'Does TAH carry over to next month?',
          a: 'No. TAH is intentionally reset each month. You\'ll need to enable it again if you want to track TAH activities for the new month.'
        },
        {
          q: 'Can my manager see my TAH data?',
          a: 'Yes. TAH data is visible to your manager just like all other facility overview content.'
        }
      ]
    },
    {
      category: 'Managers & Teams',
      icon: <Users className="w-4 h-4" />,
      questions: [
        {
          q: 'How do I invite my manager?',
          a: 'Inside a tactical click "Invite Manager" and search by name or email (type at least 3 characters). Select them from the results and send the invitation.'
        },
        {
          q: 'What can my manager see and do?',
          a: 'Managers can view all sections of your tactical, edit facility overview content (marked as "Manager Modified"), but cannot add/delete facility tabs. They can also initiate 5 Star reviews on you.'
        },
        {
          q: 'How do I accept a subordinate\'s invitation?',
          a: 'Pending invitations appear on your Tacticals dashboard. Click "Accept" to add them to your team, or "Decline" to reject the request.'
        },
        {
          q: 'How do I remove someone from my team?',
          a: 'Contact your system administrator to remove manager relationships. This cannot be done directly through the app.'
        }
      ]
    },
    {
      category: 'Monthly Rollover',
      icon: <BookOpen className="w-4 h-4" />,
      questions: [
        {
          q: 'What happens at the end of the month?',
          a: 'Your current tactical is automatically archived. You can then create a new tactical for the new month.'
        },
        {
          q: 'Do my answers carry over?',
          a: 'Scorecard statuses and facility overview content carry over. Lightning Round priorities are cleared (fresh start each month). TAH is disabled.'
        },
        {
          q: 'Can I edit an archived tactical?',
          a: 'No. Archived tacticals are read-only for historical reference. Make sure to complete your updates before month-end — autosave will help, but the rollover happens at the calendar boundary.'
        }
      ]
    },
    {
      category: 'Saving & Data Safety',
      icon: <CheckCircle2 className="w-4 h-4" />,
      questions: [
        {
          q: 'What does the autosave indicator mean?',
          a: 'Grey "Autosave on" = no changes yet. Amber "Unsaved changes" = you have edits that haven\'t been saved yet. Blue "Saving…" = the save is in flight. Green "All changes saved" = you\'re safe. Red "Autosave failed" = something went wrong — click the manual Save button to retry, or check your connection.'
        },
        {
          q: 'Will I lose work if I close the tab?',
          a: 'The browser will warn you if you have unsaved changes. The system also attempts a final fire-and-forget save when you navigate away. To be 100% safe, wait for the "All changes saved" indicator before closing.'
        },
        {
          q: 'Does autosave work for 5 Star reviews too?',
          a: 'Not currently. 5 Star ratings save when you click the submit button on each step. The Tactical editor is where autosave is active.'
        }
      ]
    }
  ];

  const filteredFaqs = searchQuery 
    ? faqs.map(category => ({
        ...category,
        questions: category.questions.filter(
          q => q.q.toLowerCase().includes(searchQuery.toLowerCase()) ||
               q.a.toLowerCase().includes(searchQuery.toLowerCase())
        )
      })).filter(category => category.questions.length > 0)
    : faqs;

  return (
    <>
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogTrigger asChild>
          <button
            type="button"
            data-testid="help-center-button"
            className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold text-white bg-gradient-to-r from-cyan-500 to-fuchsia-500 shadow-md hover:shadow-lg hover:from-cyan-600 hover:to-fuchsia-600 active:translate-y-px transition focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-fuchsia-300"
          >
            <BookOpen className="w-4 h-4" />
            Help &amp; Tutorials
          </button>
        </DialogTrigger>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-hidden p-0">
          <div className="flex h-[80vh]">
            {/* Sidebar */}
            <div className="w-56 bg-slate-50 border-r p-4 flex flex-col">
              <DialogHeader className="mb-4">
                <DialogTitle className="text-lg flex items-center gap-2">
                  <BookOpen className="w-5 h-5 text-teal-600" />
                  Help Center
                </DialogTitle>
              </DialogHeader>
              
              <nav className="space-y-1 flex-1">
                <button
                  onClick={() => setActiveSection('whats-new')}
                  className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    activeSection === 'whats-new' 
                      ? 'bg-teal-100 text-teal-700' 
                      : 'text-slate-600 hover:bg-slate-100'
                  }`}
                >
                  <Sparkles className="w-4 h-4" />
                  What's New
                </button>
                <button
                  onClick={() => setActiveSection('faq')}
                  className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    activeSection === 'faq' 
                      ? 'bg-teal-100 text-teal-700' 
                      : 'text-slate-600 hover:bg-slate-100'
                  }`}
                >
                  <MessageSquare className="w-4 h-4" />
                  FAQ
                </button>
                <button
                  onClick={() => window.open('/docs/user-manual.html', '_blank')}
                  className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-100 transition-colors"
                >
                  <FileText className="w-4 h-4" />
                  Full User Guide
                  <ChevronRight className="w-3 h-3 ml-auto" />
                </button>
              </nav>
              
              <div className="pt-4 border-t mt-4">
                <p className="text-xs text-slate-500 text-center">
                  TMC Growth Hub
                </p>
                <p className="text-xs text-slate-400 text-center">
                  v3.0 • May 2026
                </p>
              </div>
            </div>
            
            {/* Content */}
            <div className="flex-1 overflow-y-auto">
              {activeSection === 'whats-new' && (
                <div className="p-6">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 bg-gradient-to-br from-teal-400 to-emerald-500 rounded-xl flex items-center justify-center">
                      <Sparkles className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h2 className="text-xl font-bold text-slate-900">What's New</h2>
                      <p className="text-sm text-slate-500">Latest features and improvements</p>
                    </div>
                  </div>
                  
                  <div className="space-y-4">
                    {whatsNew.map((item) => (
                      <div 
                        key={item.id}
                        className="p-4 bg-white border rounded-xl hover:shadow-md transition-shadow"
                      >
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <Badge className={`${item.badgeColor} text-white text-xs`}>
                              {item.badge}
                            </Badge>
                            <span className="text-xs text-slate-400">{item.date}</span>
                          </div>
                        </div>
                        <h3 className="font-semibold text-slate-900 mb-1">{item.title}</h3>
                        <p className="text-sm text-slate-600">{item.description}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {activeSection === 'faq' && (
                <div className="p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-gradient-to-br from-blue-400 to-indigo-500 rounded-xl flex items-center justify-center">
                      <MessageSquare className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h2 className="text-xl font-bold text-slate-900">Frequently Asked Questions</h2>
                      <p className="text-sm text-slate-500">Find answers to common questions</p>
                    </div>
                  </div>
                  
                  {/* Search */}
                  <div className="relative mb-6">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      placeholder="Search FAQs..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-9"
                    />
                    {searchQuery && (
                      <button
                        onClick={() => setSearchQuery('')}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                  
                  {/* FAQ Categories */}
                  <div className="space-y-6">
                    {filteredFaqs.map((category, catIndex) => (
                      <div key={catIndex}>
                        <div className="flex items-center gap-2 mb-3 text-slate-700">
                          <div className="w-6 h-6 bg-slate-100 rounded flex items-center justify-center">
                            {category.icon}
                          </div>
                          <h3 className="font-semibold">{category.category}</h3>
                        </div>
                        
                        <div className="space-y-2 ml-8">
                          {category.questions.map((faq, faqIndex) => {
                            const faqId = `${catIndex}-${faqIndex}`;
                            const isExpanded = expandedFaq === faqId;
                            
                            return (
                              <div 
                                key={faqIndex}
                                className="border rounded-lg overflow-hidden"
                              >
                                <button
                                  onClick={() => setExpandedFaq(isExpanded ? null : faqId)}
                                  className="w-full flex items-center justify-between p-3 text-left bg-white hover:bg-slate-50 transition-colors"
                                >
                                  <span className="font-medium text-sm text-slate-700">{faq.q}</span>
                                  <ChevronRight className={`w-4 h-4 text-slate-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                                </button>
                                {isExpanded && (
                                  <div className="px-3 pb-3 pt-1 bg-slate-50 border-t">
                                    <p className="text-sm text-slate-600">{faq.a}</p>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                    
                    {filteredFaqs.length === 0 && (
                      <div className="text-center py-8 text-slate-500">
                        <MessageSquare className="w-8 h-8 mx-auto mb-2 opacity-50" />
                        <p>No FAQs found matching "{searchQuery}"</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default HelpCenter;
